import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Check, AlertCircle } from 'lucide-react';
import { useContent } from '../../lib/content';
import { api, pickLocale, formatApiError } from '../../lib/api';

export default function Contact() {
  const { content, locale } = useContent();
  const data = content.contact || {};
  const fields = data.fields || {};
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', use_case: '', message: '', website: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | ok | err
  const [error, setError] = useState('');
  const mountedAt = useRef(Date.now());

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      const elapsed_ms = Date.now() - mountedAt.current;
      const { data: res } = await api.post('/contact', { ...form, locale, elapsed_ms });
      if (res?.ok) {
        setStatus('ok');
        setForm({ name: '', email: '', company: '', role: '', use_case: '', message: '', website: '' });
      } else {
        setStatus('err');
        setError(locale === 'es' ? 'Inténtalo de nuevo más tarde.' : 'Please try again later.');
      }
    } catch (err) {
      setStatus('err');
      setError(formatApiError(err.response?.data?.detail) || (locale === 'es' ? 'Error de red.' : 'Network error.'));
    }
  };

  const ph = (k) => pickLocale(fields[k], locale, '');

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section bg-bg"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <p className="text-eyebrow mb-4">{pickLocale(data.eyebrow, locale)}</p>
          <h2 className="font-display text-5xl md:text-7xl lg:text-[7vw] tracking-tight leading-[0.92] mb-8 text-ink" data-testid="contact-title">
            {pickLocale(data.title, locale)}
          </h2>
          <p className="text-ink-muted text-base md:text-lg max-w-md leading-relaxed mb-10">
            {pickLocale(data.subtitle, locale)}
          </p>
          {data.contact_email && (
            <div className="border-t border-line pt-6 inline-block">
              <div className="text-eyebrow mb-2">{locale === 'es' ? 'O escríbenos' : 'Or email us'}</div>
              <a href={`mailto:${data.contact_email}`} className="font-display text-2xl md:text-3xl text-ink hover:text-acid transition-colors">
                {data.contact_email}
              </a>
            </div>
          )}
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-2"
        >
          {/* Honeypot — visually hidden, off-screen, NOT display:none (some bots skip those). */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
            <label aria-hidden="true">
              Website (do not fill)
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={onChange('website')}
                data-testid="contact-input-website"
                name="website"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <div>
              <label className="text-eyebrow mb-1 block">{ph('name') || 'Nombre'}</label>
              <input data-testid="contact-input-name" required type="text" value={form.name} onChange={onChange('name')} className="input-bare" placeholder="Helena Vidal" />
            </div>
            <div>
              <label className="text-eyebrow mb-1 block">{ph('email') || 'Email'}</label>
              <input data-testid="contact-input-email" required type="email" value={form.email} onChange={onChange('email')} className="input-bare" placeholder="helena@brand.com" />
            </div>
            <div>
              <label className="text-eyebrow mb-1 block">{ph('company') || 'Empresa'}</label>
              <input data-testid="contact-input-company" type="text" value={form.company} onChange={onChange('company')} className="input-bare" placeholder="Atelier N" />
            </div>
            <div>
              <label className="text-eyebrow mb-1 block">{ph('role') || 'Cargo'}</label>
              <input data-testid="contact-input-role" type="text" value={form.role} onChange={onChange('role')} className="input-bare" placeholder="Creative Director" />
            </div>
          </div>

          <div className="pt-4">
            <label className="text-eyebrow mb-1 block">{ph('use_case') || '¿Qué quieres conseguir?'}</label>
            <input data-testid="contact-input-use_case" type="text" value={form.use_case} onChange={onChange('use_case')} className="input-bare" placeholder="Catalog, lookbook, video…" />
          </div>

          <div className="pt-4">
            <label className="text-eyebrow mb-1 block">{ph('message') || 'Mensaje'}</label>
            <textarea data-testid="contact-input-message" rows={3} value={form.message} onChange={onChange('message')} className="input-bare resize-none" placeholder="..." />
          </div>

          <div className="pt-8 flex flex-col gap-4">
            <button
              type="submit"
              disabled={status === 'loading'}
              data-testid="contact-submit"
              className="btn-acid magnetic w-full md:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading'
                ? (locale === 'es' ? 'Enviando…' : 'Sending…')
                : (<>{ph('submit') || (locale === 'es' ? 'Enviar' : 'Send')} <Send size={14} /></>)}
            </button>

            {status === 'ok' && (
              <div data-testid="contact-success" className="flex items-center gap-2 text-acid font-mono text-sm uppercase tracking-[0.18em]">
                <Check size={16} /> {locale === 'es' ? 'Recibido. Te contestaremos en menos de 24h.' : 'Received. We\'ll be in touch within 24h.'}
              </div>
            )}
            {status === 'err' && (
              <div data-testid="contact-error" className="flex items-center gap-2 text-danger font-mono text-sm uppercase tracking-[0.18em]">
                <AlertCircle size={16} /> {error}
              </div>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  );
}
